'use client';

/**
 * Live insets for the "motivation" reference screen (canvas 1672×941).
 *
 * The bottom explanation panel becomes live for `causeStep` (title, what
 * happens inside, intervention + button). The painted stage pill 4 is muted
 * when another link is selected, and a 1 px lime ring marks the selected pill.
 * The dioramas, arrows and the connector lines stay painted.
 */

import { useRef, type CSSProperties } from 'react';
import { rectStyle, SCENE_CANVAS, type Rect } from '../../types';
import type { InsetProps } from '../index';
import styles from './motivation.module.css';

const C = SCENE_CANVAS.motivation;

const PANEL: Rect = [376, 588, 1032, 184];
const INTERVENTION_ID = 'motivation-intervention';

/** Painted stage pills, measured on the PNG (pill 4 is the tall, active one). */
const PILLS: readonly Rect[] = [
  [44, 250, 150, 56],
  [284, 250, 154, 56],
  [496, 250, 166, 56],
  [726, 244, 210, 68],
  [1003, 250, 150, 56],
  [1246, 250, 134, 56],
  [1464, 250, 147, 56],
];
const PAINTED_ACTIVE = 3;
/** Cover for the painted lime pill 4 (a little wider than the pill for its glow). */
const PILL4_COVER: Rect = [722, 240, 218, 76];

interface StageCopy {
  title: { ru: string; en: string };
  desc: { ru: string; en: string };
  inside: readonly [{ ru: string; en: string }, { ru: string; en: string }, { ru: string; en: string }];
  intervention: { ru: string; en: string };
}

const STAGES: readonly StageCopy[] = [
  {
    title: { ru: 'Пропущена концепция', en: 'A missed concept' },
    desc: { ru: 'Ученик пропустил объяснение или не понял его сразу. Дальше всё строится на пустом месте.', en: 'The learner missed an explanation or did not get it the first time. Everything after rests on a gap.' },
    inside: [
      { ru: 'Термин знаком, смысл нет', en: 'The term is familiar, the meaning is not' },
      { ru: 'Пример повторить можно, перенести нельзя', en: 'Can repeat an example, cannot transfer it' },
      { ru: 'Вопросы кажутся неуместными', en: 'Asking feels out of place' },
    ],
    intervention: { ru: 'Вернитесь к концепции коротко и наглядно. Один пример, один вопрос на понимание, без оценки.', en: 'Revisit the concept briefly and visually. One example, one understanding check, no grade.' },
  },
  {
    title: { ru: 'Пробел в предпосылках', en: 'A gap in prerequisites' },
    desc: { ru: 'Новая тема опирается на то, что не было освоено раньше. Объяснение звучит правильно, но не держится.', en: 'The new topic rests on something never mastered. The explanation sounds right but does not hold.' },
    inside: [
      { ru: 'Шаги решения не складываются', en: 'Solution steps do not connect' },
      { ru: 'Трудность приписывается новой теме', en: 'The difficulty is blamed on the new topic' },
      { ru: 'Растёт нагрузка на память', en: 'Memory load keeps growing' },
    ],
    intervention: { ru: 'Проверьте предпосылку одной задачей. Если она не держится, закройте её до новой темы.', en: 'Check the prerequisite with one task. If it does not hold, close it before the new topic.' },
  },
  {
    title: { ru: 'Повторяющиеся ошибки', en: 'Repeated errors' },
    desc: { ru: 'Одна и та же ошибка воспроизводится из задания в задание. Каждое повторение закрепляет неверный ход.', en: 'The same error repeats from task to task. Each repetition reinforces the wrong move.' },
    inside: [
      { ru: 'Ошибка не осознаётся как ошибка', en: 'The error is not seen as an error' },
      { ru: 'Красные пометки без объяснения', en: 'Red marks without an explanation' },
      { ru: 'Появляется избегание темы', en: 'The topic starts to be avoided' },
    ],
    intervention: { ru: 'Разберите одну ошибку вместе, назовите её и дайте два задания, где она видна сразу.', en: 'Work through one error together, name it, and give two tasks where it shows immediately.' },
  },
  {
    title: { ru: 'Усилия без результата', en: 'Effort without results' },
    desc: { ru: 'Ученик старается, решает задания, но не видит прогресса. Возникает ощущение, что сколько ни делай — не получается.', en: 'The learner tries and solves tasks but sees no progress. It starts to feel like nothing works no matter what.' },
    inside: [
      { ru: 'Нет видимой обратной связи', en: 'No visible feedback' },
      { ru: 'Результат неочевиден', en: 'The result is not obvious' },
      { ru: 'Появляется чувство тупика', en: 'A sense of a dead end' },
    ],
    intervention: { ru: 'Покажите микро-прогресс и смысл каждого шага. Дайте задание, где успех достижим уже сегодня.', en: 'Show micro progress and the point of each step. Give a task where success is reachable today.' },
  },
  {
    title: { ru: 'Падает уверенность', en: 'Confidence drops' },
    desc: { ru: 'После серии неудач ученик перестаёт верить, что справится. Даже посильные задания кажутся рискованными.', en: 'After a run of failures the learner stops believing they can cope. Even doable tasks feel risky.' },
    inside: [
      { ru: '«У меня не получится» до начала', en: '“I will not manage” before starting' },
      { ru: 'Отказ от сложных задач', en: 'Refusing harder tasks' },
      { ru: 'Успех объясняется случайностью', en: 'Success is put down to luck' },
    ],
    intervention: { ru: 'Серия из трёх заведомо посильных шагов с быстрой обратной связью. Отметьте, что именно получилось.', en: 'A series of three clearly doable steps with quick feedback. Name exactly what worked.' },
  },
  {
    title: { ru: 'Теряется контроль', en: 'Control is lost' },
    desc: { ru: 'Ученик не понимает, что от него ждут и что делать дальше. Учёба превращается в набор случайных требований.', en: 'The learner no longer knows what is expected or what to do next. Study becomes a set of random demands.' },
    inside: [
      { ru: 'Непонятны критерии успеха', en: 'Success criteria are unclear' },
      { ru: 'Следующий шаг не виден', en: 'The next step is not visible' },
      { ru: 'Помощь приходит слишком поздно', en: 'Help arrives too late' },
    ],
    intervention: { ru: 'Верните понятный маршрут: один ближайший шаг, ясный критерий и право выбрать порядок.', en: 'Restore a clear route: one next step, a clear criterion, and a choice of order.' },
  },
  {
    title: { ru: 'Пропадает мотивация', en: 'Motivation disappears' },
    desc: { ru: 'Итог цепочки: усилия больше не связаны с результатом, и ученик перестаёт пробовать.', en: 'The end of the chain: effort is no longer linked to results, and the learner stops trying.' },
    inside: [
      { ru: 'Формальное выполнение', en: 'Going through the motions' },
      { ru: 'Интерес заменён избеганием', en: 'Interest replaced by avoidance' },
      { ru: 'Оценка теряет значение', en: 'Grades stop mattering' },
    ],
    intervention: { ru: 'Начните с самого раннего звена: найдите пропущенную концепцию и покажите первый видимый успех.', en: 'Start from the earliest link: find the missed concept and show the first visible success.' },
  },
];

const T = {
  inside: { ru: 'Что происходит внутри', en: 'What happens inside' },
  intervention: { ru: 'Вмешательство StudyNinja', en: 'StudyNinja intervention' },
  apply: { ru: 'Применить вмешательство', en: 'Apply intervention' },
  applyLabel: { ru: 'Применить вмешательство и перейти к выбору цели', en: 'Apply intervention and continue to goal selection' },
  link: { ru: 'Звено', en: 'Link' },
} as const;

function useChanged(value: unknown): boolean {
  const initial = useRef(value);
  const changed = useRef(false);
  if (!Object.is(value, initial.current)) changed.current = true;
  return changed.current;
}

function TargetIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="2.2" fill="currentColor" />
      <path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function NinjaIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.5 11.5h13" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
      <circle cx="9" cy="11.5" r="1.1" fill="#071019" />
      <circle cx="15" cy="11.5" r="1.1" fill="#071019" />
    </svg>
  );
}

export default function MotivationInsets({ lang, state, navigate, spotlight, onInteraction }: InsetProps) {
  const step = Math.min(STAGES.length - 1, Math.max(0, state.causeStep));
  const stage = STAGES[step];
  const live = useChanged(step);
  const pick = <V,>(pair: { ru: V; en: V }): V => (lang === 'ru' ? pair.ru : pair.en);
  const applyLabel = pick(T.applyLabel);
  const applySpot = spotlight === INTERVENTION_ID;

  const ring = PILLS[step];
  const ringRect: Rect = [ring[0] - 3, ring[1] - 3, ring[2] + 6, ring[3] + 6];

  return (
    <div className={styles.root}>
      {/* painted lime pill 4, muted when another link is selected */}
      <div className={styles.pillCover} style={rectStyle(PILL4_COVER, C)} data-on={step !== PAINTED_ACTIVE ? 'true' : undefined} aria-hidden="true">
        <span className={styles.pillMuted}>
          <b>4</b>
          <span>{pick(STAGES[PAINTED_ACTIVE].title)}</span>
        </span>
      </div>

      <i key={`ring-${step}`} className={styles.pillRing} style={rectStyle(ringRect, C)} data-live={live || undefined} data-tall={step === PAINTED_ACTIVE ? 'true' : undefined} aria-hidden="true" />

      <section className={styles.panel} style={rectStyle(PANEL, C)} data-live={live || undefined} aria-label={`${pick(T.link)} ${step + 1}: ${pick(stage.title)}`}>
        <div key={step} className={styles.body}>
          <div className={styles.cause}>
            <span className={styles.causeIcon} style={{ '--i': 0 } as CSSProperties}><TargetIcon /></span>
            <div className={styles.causeText}>
              <h3 className={styles.title} style={{ '--i': 0 } as CSSProperties}>{pick(stage.title)}</h3>
              <p className={styles.desc} style={{ '--i': 1 } as CSSProperties}>{pick(stage.desc)}</p>
            </div>
          </div>

          <div className={styles.inside}>
            <h4 className={styles.subhead} style={{ '--i': 1 } as CSSProperties}>{pick(T.inside)}</h4>
            <ul className={styles.bullets}>
              {stage.inside.map((item, index) => (
                <li key={item.ru} style={{ '--i': index + 2 } as CSSProperties}>{pick(item)}</li>
              ))}
            </ul>
          </div>

          <div className={styles.intervention}>
            <span className={styles.ninja} style={{ '--i': 2 } as CSSProperties}><NinjaIcon /></span>
            <h4 className={styles.subheadLime} style={{ '--i': 2 } as CSSProperties}>{pick(T.intervention)}</h4>
            <p className={styles.interventionText} style={{ '--i': 3 } as CSSProperties}>{pick(stage.intervention)}</p>
            <button
              type="button"
              className={styles.apply}
              style={{ '--i': 4 } as CSSProperties}
              data-hotspot={INTERVENTION_ID}
              data-spotlight={applySpot ? 'true' : undefined}
              aria-label={applyLabel}
              onClick={() => {
                onInteraction?.(applyLabel);
                navigate('teacher-goal');
              }}
            >
              <span>{pick(T.apply)}</span>
              <span aria-hidden="true">→</span>
              {applySpot && <i className={styles.pulse} aria-hidden="true" />}
            </button>
          </div>
        </div>
        <i key={`relight-${step}`} className={styles.relight} aria-hidden="true" />
      </section>
    </div>
  );
}
